import { AUTH_TOKEN_COOKIE, FALLBACK_TOKEN_COOKIES, USER_STORAGE_KEY } from "@/core/constants/keys";
import { getCookie, setCookie, removeCookie } from "@/core/utils/cookie";
import { clearUserPhotoCache } from "@/hooks/use-user-photo";
import type { JwtPayload, UserData } from "./auth.types";

const SCOPE_HUB_WEB = "workspace";
const SCOPE_OBRA_CAMPO = "ObraCampo";

export { SCOPE_HUB_WEB, SCOPE_OBRA_CAMPO };

export function isInIframe(): boolean {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    return true;
  }
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now();
}

export function getAuthToken(): string | null {
  let token = getCookie(AUTH_TOKEN_COOKIE);
  if (token) return token;

  for (const fallback of FALLBACK_TOKEN_COOKIES) {
    token = getCookie(fallback);
    if (token) return token;
  }

  return null;
}

const DEFAULT_COOKIE_MAX_AGE_SECONDS = 3600;

/**
 * Validade do cookie, em segundos, a partir do `exp` do JWT. Token ilegível, sem `exp` ou
 * já vencido cai no padrão de 1h (a sessão por inatividade é tratada à parte, no front).
 */
function getCookieMaxAgeFromToken(token: string): number {
  const exp = decodeJwt(token)?.exp;
  if (typeof exp !== "number" || !Number.isFinite(exp)) return DEFAULT_COOKIE_MAX_AGE_SECONDS;

  const remainingSeconds = Math.floor(exp - Date.now() / 1000);
  return remainingSeconds > 0 ? remainingSeconds : DEFAULT_COOKIE_MAX_AGE_SECONDS;
}

export function saveAuthToken(token: string, expiresIn?: number): void {
  const inIframe = isInIframe();
  const options: Parameters<typeof setCookie>[2] = {
    path: "/",
    secure: true,
    sameSite: inIframe ? "none" : "strict",
  };

  if (expiresIn) {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + expiresIn * 1000);
    options.expires = expirationDate;
  } else {
    options.maxAge = getCookieMaxAgeFromToken(token);
  }

  setCookie(AUTH_TOKEN_COOKIE, token, options);
}

const authClearedListeners = new Set<() => void>();

/** Avisa quando a sessão é encerrada (logout ou 401), p.ex. para descartar caches por sessão. */
export function onAuthCleared(listener: () => void): () => void {
  authClearedListeners.add(listener);
  return () => {
    authClearedListeners.delete(listener);
  };
}

export function clearAuthToken(): void {
  authClearedListeners.forEach((listener) => listener());
  removeCookie(AUTH_TOKEN_COOKIE);
  for (const fallback of FALLBACK_TOKEN_COOKIES) {
    removeCookie(fallback);
  }
  localStorage.removeItem(USER_STORAGE_KEY);
  // A foto em cache é do usuário da sessão que acabou (logout ou 401).
  clearUserPhotoCache();
}

export function extractUserData(token: string): UserData | null {
  const payload = decodeJwt(token);
  if (!payload) return null;

  const scopes = payload.scope?.split(" ") ?? [];

  return {
    name: payload.name ?? payload.preferred_username ?? payload.email ?? "Usuário",
    email: payload.email,
    preferredUsername: payload.preferred_username,
    organizationId: payload.organization_id,
    area: payload.department ?? payload.area ?? payload.job_title,
    sub: payload.sub,
    scopes,
  };
}

export function persistUserData(user: UserData): void {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function getPersistedUser(): UserData | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserData;
  } catch {
    return null;
  }
}

export function hasScope(token: string | null, scope: string): boolean {
  if (!token) return false;
  const payload = decodeJwt(token);
  return payload?.scope?.includes(scope) ?? false;
}

export function hasAnyScope(token: string | null, scopes: string[]): boolean {
  return scopes.some((s) => hasScope(token, s));
}

export function hasScopeObraCampo(token: string | null): boolean {
  return hasScope(token, SCOPE_OBRA_CAMPO);
}
